import CESAppInfo from "../CESAppInfo";
import { ValueElement } from "./Base";
import { TextInputElement, TextInputType } from "./TextInput";

declare let grecaptcha: any;
declare let window: any;

export default class RecaptchaEnterpriseElement extends ValueElement {
  private static singleton: RecaptchaEnterpriseElement | null = null;
  private instance: HTMLDivElement;
  private _captchaToken: string | null = null;
  private captchaFieldElement: TextInputElement | null = null; private isRendered: boolean = false;
  private tokenPromise: Promise<string> | null = null;
  private scriptLoadPromise: Promise<void> | null = null;
  private readonly MAX_RETRIES = 3;
  private retryCount = 0;
  private constructor(name: string) {
    super(name);

    this.instance = document.createElement("div");
    this.instance.setAttribute("id", `recaptcha-container-${name}`);
    this.instance.classList.add("recaptcha-enterprise-container");

    this.captchaFieldElement = new TextInputElement(`${name}-field`, "", TextInputType.Hidden);
    this.instance.appendChild(this.captchaFieldElement.element);
  }

  private cleanupExistingElements() {
    // First remove any existing reCAPTCHA elements
    document.querySelectorAll('script[src*="recaptcha"]').forEach(script => {
      script.remove();
    });

    // Remove any existing reCAPTCHA iframes and badges
    document.querySelectorAll('iframe[src*="recaptcha"], .grecaptcha-badge').forEach(element => {
      element.remove();
    });

    // Clean up grecaptcha instance
    if (typeof grecaptcha !== 'undefined') {
      // @ts-ignore
      delete window.grecaptcha;
    }

    this.isRendered = false;
    this.scriptLoadPromise = null;
  }

  private loadScript(): Promise<void> {
    if (this.scriptLoadPromise) {
      return this.scriptLoadPromise;
    }

    this.scriptLoadPromise = new Promise((resolve, reject) => {
      if (!CESAppInfo.reCaptchakey) {
        reject(new Error("reCAPTCHA site key not configured"));
        return;
      }

      const recaptchaScriptId = "recaptchaScriptEnterprise";
      const existingScript = document.getElementById(recaptchaScriptId);

      if (existingScript) {
        // If script exists but grecaptcha isn't defined, remove and reload
        if (typeof grecaptcha === 'undefined' || !grecaptcha.enterprise) {
          existingScript.remove();
        } else {
          resolve();
          return;
        }
      }

      const dashIdx = CESAppInfo.locale.indexOf("-");
      const hl = CESAppInfo.locale.substring(0, dashIdx > 0 ? dashIdx : CESAppInfo.locale.length);

      const script = document.createElement("script");
      script.id = recaptchaScriptId;
      script.src = `https://www.google.com/recaptcha/enterprise.js?render=${CESAppInfo.reCaptchakey}&hl=${hl}`;
      script.async = true;
      script.defer = true;

      let timeoutId: number | null = null;

      const handleLoad = () => {
        if (timeoutId) clearTimeout(timeoutId);
        if (typeof grecaptcha === 'undefined' || !grecaptcha.enterprise) {
          reject(new Error('reCAPTCHA failed to load'));
          return;
        }
        grecaptcha.enterprise.ready(() => {
          this.isRendered = true;
          resolve();
        });
      };

      const handleError = () => {
        if (timeoutId) clearTimeout(timeoutId);
        this.scriptLoadPromise = null;
        reject(new Error('Failed to load reCAPTCHA script'));
      };

      timeoutId = window.setTimeout(() => {
        script.removeEventListener('load', handleLoad);
        script.removeEventListener('error', handleError);
        this.scriptLoadPromise = null;
        reject(new Error('reCAPTCHA script load timeout'));
      }, 10000);

      script.addEventListener('load', handleLoad);
      script.addEventListener('error', handleError);

      document.head.appendChild(script);
    });

    return this.scriptLoadPromise;
  }

  public static getInstance(name: string = "recaptcha"): RecaptchaEnterpriseElement | null {
    if (!CESAppInfo.reCaptchakey) {
      console.error("Cannot initialize reCAPTCHA Enterprise: missing site key");
      return null;
    }

    try {
      if (!RecaptchaEnterpriseElement.singleton || RecaptchaEnterpriseElement.singleton.name !== name) {
        RecaptchaEnterpriseElement.singleton = new RecaptchaEnterpriseElement(name);
        // Immediately try to load the script to catch any early issues
        RecaptchaEnterpriseElement.singleton.loadScript()
          .catch(error => {
            console.error("Failed to load reCAPTCHA script:", error);
            RecaptchaEnterpriseElement.singleton = null;
          });
      }

      return RecaptchaEnterpriseElement.singleton;
    } catch (error) {
      console.error("Failed to initialize RecaptchaEnterpriseElement:", error);
      return null;
    }
  }

  get element(): HTMLElement {
    return this.instance;
  }

  get value(): Promise<string | null> {
    return this.ensureFreshToken();
  }

  set value(_val: any) { }

  private validateToken(token: string | null): boolean {
    if (!token || typeof token !== 'string') return false;
    if (token.length < 20) return false;
    return true;
  }

  private async generateNewToken(operation?: string): Promise<string> {
    try {
      await this.loadScript();

      if (!grecaptcha?.enterprise) {
        throw new Error("Enterprise reCAPTCHA not initialized");
      }

      const token = await grecaptcha.enterprise.execute(CESAppInfo.reCaptchakey, {
        action: operation || 'submit'
      }); if (this.validateToken(token)) {
        this._captchaToken = token;
        if (this.captchaFieldElement) {
          this.captchaFieldElement.value = token;
        }
        this.onChange();

        // Reset retry count on success
        this.retryCount = 0;

        return token;
      } else {
        throw new Error("Invalid token received from reCAPTCHA");
      }
    } catch (error) {      this.retryCount++;
      if (this.retryCount >= this.MAX_RETRIES) {
        this.cleanupExistingElements();
        this.retryCount = 0;
        throw new Error("Failed to generate reCAPTCHA token after multiple attempts");
      }

      // Clear token state
      this._captchaToken = null;
      if (this.captchaFieldElement) {
        this.captchaFieldElement.value = "";
      }

      throw error;
    }
  }

  async ensureFreshToken(operation?: string): Promise<string | null> {
    try {
      // Always generate a new token, regardless of existing token
      this.resetTokenValue();

      // Generate a new token
      this.tokenPromise = this.generateNewToken(operation);

      const token = await this.tokenPromise;
      return token;
    } catch (_error) {
      this.tokenPromise = null;
      return null;
    }
  }

  submit = async (operation?: string): Promise<string | null> => this.ensureFreshToken(operation);
  resetTokenValue = () => {
    this._captchaToken = null;
    this.tokenPromise = null;
    if (this.captchaFieldElement) {
      this.captchaFieldElement.value = "";
    }
  };

  reset = () => {
    this.resetTokenValue();
  };

  isApplied = () => this.isRendered;

  isVerified = () => this._captchaToken !== null && this.validateToken(this._captchaToken);

  dispose(): void {
    this.resetTokenValue();
    this.cleanupExistingElements();
  }

  disable(): void {
    this.resetTokenValue();
  }
}
